/**
 * Tiny IndexedDB key/value cache with a per-entry TTL.
 *
 * Used for payloads too large for localStorage (parcel GeoJSON for a
 * tenant boundary is multi-MB). API: `idbGet(store, key)` returns the
 * cached value or `null` if absent / expired; `idbSet(store, key, value,
 * ttlMs)` writes one. Stale entries are deleted lazily on read.
 */

const DB_NAME = 'fpa-cache';
// v2 added the 'members' store (per-member cache for the parcels map's
// hazard-colour pipeline).
const DB_VERSION = 2;
// Stores we know about up-front. Adding a new one requires a DB version
// bump — keep this list small and intentional.
const STORES = ['parcels', 'members'];

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    dbPromise = Promise.reject(new Error('IndexedDB not available'));
    return dbPromise;
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      STORES.forEach((name) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name);
        }
      });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const tx = async (store, mode) => {
  const db = await openDb();
  return db.transaction(store, mode).objectStore(store);
};

export const idbGet = async (store, key) => {
  try {
    const os = await tx(store, 'readonly');
    return await new Promise((resolve, reject) => {
      const r = os.get(key);
      r.onsuccess = () => {
        const entry = r.result;
        if (!entry) return resolve(null);
        if (typeof entry.expiresAt === 'number' && entry.expiresAt < Date.now()) {
          // Stale — drop it and return miss. Fire-and-forget.
          idbDelete(store, key).catch(() => {});
          return resolve(null);
        }
        resolve(entry.value);
      };
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    console.warn('idbGet failed:', err);
    return null;
  }
};

export const idbSet = async (store, key, value, ttlMs) => {
  try {
    const os = await tx(store, 'readwrite');
    const entry = {
      value,
      expiresAt: typeof ttlMs === 'number' ? Date.now() + ttlMs : null,
    };
    await new Promise((resolve, reject) => {
      const r = os.put(entry, key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    console.warn('idbSet failed:', err);
  }
};

export const idbClear = async (store) => {
  try {
    const os = await tx(store, 'readwrite');
    await new Promise((resolve, reject) => {
      const r = os.clear();
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    console.warn('idbClear failed:', err);
  }
};

// Returns the number of entries currently stored — used by the Settings
// page to show how much is cached before clearing. Cheap COUNT op, no
// values transferred.
export const idbCount = async (store) => {
  try {
    const os = await tx(store, 'readonly');
    return await new Promise((resolve, reject) => {
      const r = os.count();
      r.onsuccess = () => resolve(r.result || 0);
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    console.warn('idbCount failed:', err);
    return 0;
  }
};

export const idbDelete = async (store, key) => {
  try {
    const os = await tx(store, 'readwrite');
    await new Promise((resolve, reject) => {
      const r = os.delete(key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    console.warn('idbDelete failed:', err);
  }
};
